import type { Dispatch, SetStateAction } from "react";

import {
  JUMP_TARGET_END,
  type FormPage,
  type FormQuestion,
  type JumpLogicEvaluator,
  type PublicFormVersion,
  VisibilityEvaluator,
} from "@kushya/shared";

import type { ValidationError as SubmissionValidationError } from "@/api/responses";
import { ResponseSubmissionError } from "@/api/responses";
import {
  emitMojoFormSubmittedEvent,
  MOJO_FORM_SUBMITTED_EVENT,
} from "@/utils/embed-events";
import { validateAnswersClientSide } from "@/utils/validation";

import {
  evaluatePageJump,
  findPageIndexForTarget,
  getStepIndexForPageIndex,
} from "./pages";

type Setter<T> = Dispatch<SetStateAction<T>>;

type AnswerRecord = Record<string, unknown>;

const isQuestionVisible = (
  question: FormQuestion,
  answers: AnswerRecord,
  visibilityEvaluator?: VisibilityEvaluator,
): boolean => {
  if (!visibilityEvaluator) {
    return true;
  }
  return visibilityEvaluator.isVisible(question, answers);
};

const findNextVisibleQuestionIndex = (params: {
  questions: FormQuestion[];
  startIndex: number;
  answers: AnswerRecord;
  visibilityEvaluator?: VisibilityEvaluator;
}): number | null => {
  const { questions, startIndex, answers, visibilityEvaluator } = params;
  const safeStart = Math.max(0, startIndex);

  if (!visibilityEvaluator) {
    return safeStart < questions.length ? safeStart : null;
  }

  for (let index = safeStart; index < questions.length; index += 1) {
    const question = questions[index];
    if (question && isQuestionVisible(question, answers, visibilityEvaluator)) {
      return index;
    }
  }

  return null;
};

const findPreviousVisibleQuestionIndex = (params: {
  questions: FormQuestion[];
  startIndex: number;
  answers: AnswerRecord;
  visibilityEvaluator?: VisibilityEvaluator;
}): number | null => {
  const { questions, startIndex, answers, visibilityEvaluator } = params;
  const safeStart = Math.min(startIndex, questions.length - 1);

  if (!visibilityEvaluator) {
    return safeStart >= 0 ? safeStart : null;
  }

  for (let index = safeStart; index >= 0; index -= 1) {
    const question = questions[index];
    if (question && isQuestionVisible(question, answers, visibilityEvaluator)) {
      return index;
    }
  }

  return null;
};

const findNextPageIndexWithVisibleQuestions = (params: {
  startPageIndex: number;
  totalPages: number;
  pageIds: string[];
  answers: AnswerRecord;
  getQuestionsForPage: (pageId: string | null | undefined) => FormQuestion[];
  visibilityEvaluator?: VisibilityEvaluator;
}): number | null => {
  const {
    startPageIndex,
    totalPages,
    pageIds,
    answers,
    getQuestionsForPage,
    visibilityEvaluator,
  } = params;

  const safeStart = Math.max(0, startPageIndex);

  for (let pageIndex = safeStart; pageIndex < totalPages; pageIndex += 1) {
    const pageId = pageIds[pageIndex];
    const pageQuestions = getQuestionsForPage(pageId);

    if (!visibilityEvaluator) {
      if (pageQuestions.length > 0) {
        return pageIndex;
      }
      continue;
    }

    const visibleQuestions = visibilityEvaluator.filterVisible(
      pageQuestions,
      answers,
    );
    if (visibleQuestions.length > 0) {
      return pageIndex;
    }
  }

  return null;
};

export async function handleSubmit(params: {
  form: PublicFormVersion;
  questions: FormQuestion[];
  answers: AnswerRecord;
  skippedQuestionRefs: string[];
  questionPath: string[];
  startTime: Date;
  hasPages: boolean;
  totalPages: number;
  totalQuestions: number;
  projectId?: string;
  formId?: string;
  visibilityEvaluator?: VisibilityEvaluator;
  submitResponse: (input: {
    projectId: string;
    formId: string;
    versionNumber: number;
    answers: AnswerRecord;
    startedAt: Date;
    skippedQuestionRefs: string[];
    questionPath: string[];
  }) => Promise<{ responseId: string }>;
  trackFormEvent: (
    projectId: string,
    formId: string,
    event: "form_completed",
    payload?: { durationMs?: number },
  ) => void | Promise<void>;
  clearDraft: () => void;

  setSubmissionError: Setter<string | null>;
  setValidationErrors: Setter<SubmissionValidationError[]>;
  setTouchedQuestions: Setter<Set<string>>;
  setFieldErrors: Setter<Map<string, string>>;
  setIsSubmitting: Setter<boolean>;
  setIsSubmitted: Setter<boolean>;
  setResponseId: Setter<string | null>;
  setCurrentStep: Setter<number>;
  setCurrentPageIndex: Setter<number>;
}) {
  const {
    form,
    questions,
    answers,
    skippedQuestionRefs,
    questionPath,
    startTime,
    hasPages,
    totalPages,
    totalQuestions,
    projectId,
    formId,
    visibilityEvaluator,
    submitResponse,
    trackFormEvent,
    clearDraft,
    setSubmissionError,
    setValidationErrors,
    setTouchedQuestions,
    setFieldErrors,
    setIsSubmitting,
    setIsSubmitted,
    setResponseId,
    setCurrentStep,
    setCurrentPageIndex,
  } = params;

  setSubmissionError(null);
  setValidationErrors([]);

  // Filter to only visible questions for validation (hidden questions should not block submission)
  const visibleQuestions = visibilityEvaluator
    ? visibilityEvaluator.filterVisible(questions, answers)
    : questions;

  const clientValidation = validateAnswersClientSide(
    visibleQuestions,
    answers,
    skippedQuestionRefs,
  );

  if (!clientValidation.valid) {
    setTouchedQuestions((prev) => {
      const next = new Set(prev);
      for (const error of clientValidation.errors) {
        next.add(error.questionRef);
      }
      return next;
    });
    setFieldErrors((prev) => {
      const next = new Map(prev);
      for (const error of clientValidation.errors) {
        next.set(error.questionRef, error.message);
      }
      return next;
    });
    setValidationErrors(clientValidation.errors);
    setSubmissionError("Please correct the errors below before submitting.");

    // Find first error in visible questions
    const firstErrorIndex = visibleQuestions.findIndex((question) =>
      clientValidation.errors.some(
        (validationError) => validationError.questionRef === question.ref,
      ),
    );

    if (firstErrorIndex >= 0) {
      // Map back to the original questions array index
      const originalIndex = questions.findIndex(
        (q) => q.ref === visibleQuestions[firstErrorIndex]?.ref,
      );
      if (originalIndex >= 0) {
        setCurrentStep(originalIndex);
      }
    }

    return;
  }

  setIsSubmitting(true);

  try {
    const result = await submitResponse({
      projectId: form.projectId,
      formId: form.formId,
      versionNumber: form.version,
      answers,
      startedAt: startTime,
      skippedQuestionRefs,
      questionPath,
    });

    setIsSubmitted(true);
    setResponseId(result.responseId);
    clearDraft();

    if (projectId && formId) {
      const durationMs = Date.now() - startTime.getTime();
      void trackFormEvent(projectId, formId, "form_completed", { durationMs });
    }

    setCurrentStep(totalQuestions);
    if (hasPages) {
      setCurrentPageIndex(totalPages);
    }

    emitMojoFormSubmittedEvent({
      type: MOJO_FORM_SUBMITTED_EVENT,
      responseId: result.responseId,
      projectId: form.projectId,
      formId: form.formId,
      versionNumber: form.version,
      projectSlug: form.projectSlug,
      formSlug: form.formSlug,
      submittedAt: new Date().toISOString(),
    });
  } catch (error) {
    if (error instanceof ResponseSubmissionError) {
      switch (error.code) {
        case "VALIDATION_ERROR": {
          const errors = error.questionErrors ?? [];
          setTouchedQuestions((prev) => {
            const next = new Set(prev);
            for (const validationError of errors) {
              next.add(validationError.questionRef);
            }
            return next;
          });
          setFieldErrors((prev) => {
            const next = new Map(prev);
            for (const validationError of errors) {
              next.set(validationError.questionRef, validationError.message);
            }
            return next;
          });
          setValidationErrors(errors);
          setSubmissionError(
            "Some answers need your attention. Please review the highlighted questions.",
          );

          const firstErrorIndex = questions.findIndex((question) =>
            errors.some(
              (validationError) => validationError.questionRef === question.ref,
            ),
          );

          if (firstErrorIndex >= 0) {
            setCurrentStep(firstErrorIndex);
          }
          break;
        }

        case "RATE_LIMIT_EXCEEDED":
          setSubmissionError(
            "Too many submission attempts. Please wait a few minutes before trying again.",
          );
          break;

        case "FORM_NOT_FOUND":
          setSubmissionError(
            "This form is no longer available. Please contact the form owner for assistance.",
          );
          break;

        case "NETWORK_ERROR":
          setSubmissionError(
            "Unable to connect. Please check your internet connection and try again.",
          );
          break;

        default:
          setSubmissionError(
            "Something went wrong. Please try again, or contact support if the problem persists.",
          );
      }
    } else {
      setSubmissionError(
        "An unexpected error occurred. Please try again, or contact support if the problem persists.",
      );
    }
  } finally {
    setIsSubmitting(false);
  }
}

export function handleBack(params: {
  hasPages: boolean;
  hasIntro: boolean;
  pageIds: string[];
  getQuestionsForPage: (pageId: string | null | undefined) => FormQuestion[];
  questions: FormQuestion[];
  answers: AnswerRecord;
  visibilityEvaluator?: VisibilityEvaluator;
  setSubmissionError: Setter<string | null>;
  setPageHistory: Setter<number[]>;
  setCurrentPageIndex: Setter<number>;
  setCurrentStep: Setter<number>;
  setQuestionHistory: Setter<number[]>;
}) {
  const {
    hasPages,
    hasIntro,
    pageIds,
    getQuestionsForPage,
    questions,
    answers,
    visibilityEvaluator,
    setSubmissionError,
    setPageHistory,
    setCurrentPageIndex,
    setCurrentStep,
    setQuestionHistory,
  } = params;

  setSubmissionError(null);

  if (hasPages) {
    setPageHistory((prev) => {
      if (prev.length === 0) {
        if (hasIntro) {
          setCurrentStep(-1);
        }
        return prev;
      }

      const previous = prev[prev.length - 1] ?? 0;
      setCurrentPageIndex(previous);
      setCurrentStep(
        getStepIndexForPageIndex(previous, pageIds, getQuestionsForPage),
      );
      return prev.slice(0, -1);
    });
    return;
  }

  setQuestionHistory((prev) => {
    if (prev.length === 0) {
      setCurrentStep((current) => {
        const previousIndex = findPreviousVisibleQuestionIndex({
          questions,
          startIndex: current - 1,
          answers,
          visibilityEvaluator,
        });

        if (previousIndex !== null) {
          return previousIndex;
        }

        if (current === 0 && hasIntro) {
          return -1;
        }

        return current;
      });
      return prev;
    }

    const nextHistory = [...prev];

    while (nextHistory.length > 0) {
      const previousStep = nextHistory.pop();
      if (previousStep === undefined) {
        continue;
      }

      const resolved = findPreviousVisibleQuestionIndex({
        questions,
        startIndex: previousStep,
        answers,
        visibilityEvaluator,
      });

      if (resolved !== null) {
        setCurrentStep(resolved);
        return nextHistory;
      }
    }

    if (hasIntro) {
      setCurrentStep(-1);
    }

    return nextHistory;
  });
}

export async function handleNext(params: {
  isSubmitting: boolean;
  isSharedMode?: boolean;
  hasIntro: boolean;
  currentStep: number;
  hasPages: boolean;
  pages: FormPage[];
  currentPageIndex: number;
  totalPages: number;
  totalQuestions: number;
  pageIds: string[];
  currentPageQuestions?: FormQuestion[];
  questions: FormQuestion[];
  answers: AnswerRecord;
  skippedQuestionRefs: string[];
  jumpEvaluator: JumpLogicEvaluator;
  visibilityEvaluator?: VisibilityEvaluator;
  getQuestionsForPage: (pageId: string | null | undefined) => FormQuestion[];
  onSubmit: () => Promise<void>;

  setCurrentStep: Setter<number>;
  setCurrentPageIndex: Setter<number>;
  setPageHistory: Setter<number[]>;
  setQuestionHistory: Setter<number[]>;
  setTouchedQuestions: Setter<Set<string>>;
  setFieldErrors: Setter<Map<string, string>>;
  setValidationErrors: Setter<SubmissionValidationError[]>;
  setSubmissionError: Setter<string | null>;
}) {
  const {
    isSubmitting,
    isSharedMode = false,
    hasIntro,
    currentStep,
    hasPages,
    pages,
    currentPageIndex,
    totalPages,
    totalQuestions,
    pageIds,
    currentPageQuestions,
    questions,
    answers,
    skippedQuestionRefs,
    jumpEvaluator,
    visibilityEvaluator,
    getQuestionsForPage,
    onSubmit,
    setCurrentStep,
    setCurrentPageIndex,
    setPageHistory,
    setQuestionHistory,
    setTouchedQuestions,
    setFieldErrors,
    setValidationErrors,
    setSubmissionError,
  } = params;

  if (isSubmitting) {
    return;
  }

  if (hasIntro && currentStep === -1) {
    setCurrentStep(0);
    return;
  }

  if (hasPages) {
    const pageQuestions = currentPageQuestions ?? [];
    if (!isSharedMode) {
      const result = validateAnswersClientSide(
        pageQuestions,
        answers,
        skippedQuestionRefs,
      );

      setTouchedQuestions((prev) => {
        const next = new Set(prev);
        pageQuestions.forEach((q) => next.add(q.ref));
        return next;
      });

      setFieldErrors((prev) => {
        const next = new Map(prev);
        pageQuestions.forEach((q) => {
          const error = result.errors.find((e) => e.questionRef === q.ref);
          if (error) {
            next.set(q.ref, error.message);
          } else {
            next.delete(q.ref);
          }
        });
        return next;
      });

      if (!result.valid) {
        setValidationErrors(result.errors);
        setSubmissionError("Please correct the errors below before continuing.");
        return;
      }
    }

    setSubmissionError(null);

    let targetRef: string | null = null;

    const currentPageId = pageIds[currentPageIndex];
    const currentPage =
      currentPageId && currentPageId !== "__unassigned"
        ? (pages.find((page) => page.id === currentPageId) ?? null)
        : null;

    if (currentPage) {
      targetRef = evaluatePageJump({
        page: currentPage,
        pageQuestions,
        answers,
        jumpEvaluator,
      });
    }

    if (targetRef) {
      if (targetRef === JUMP_TARGET_END) {
        await onSubmit();
        return;
      }

      const targetPageIndex = findPageIndexForTarget(targetRef, pageIds);
      if (targetPageIndex === null) {
        // eslint-disable-next-line no-console
        console.warn(
          `Jump logic target "${targetRef}" was not found in this form.`,
        );
      } else if (targetPageIndex <= currentPageIndex) {
        // eslint-disable-next-line no-console
        console.warn(
          `Jump logic attempted to navigate backwards from page "${currentPageId ?? ""}" to target "${targetRef}". This is not supported.`,
        );
      } else if (targetPageIndex >= totalPages) {
        // eslint-disable-next-line no-console
        console.warn(`Jump logic target "${targetRef}" is out of bounds.`);
      } else {
        const nextPageIndex = findNextPageIndexWithVisibleQuestions({
          startPageIndex: targetPageIndex,
          totalPages,
          pageIds,
          answers,
          getQuestionsForPage,
          visibilityEvaluator,
        });

        if (nextPageIndex === null) {
          await onSubmit();
          return;
        }

        setPageHistory((prev) => [...prev, currentPageIndex]);
        setCurrentPageIndex(nextPageIndex);
        setCurrentStep(
          getStepIndexForPageIndex(nextPageIndex, pageIds, getQuestionsForPage),
        );
        return;
      }
    }

    const nextPageIndex = findNextPageIndexWithVisibleQuestions({
      startPageIndex: currentPageIndex + 1,
      totalPages,
      pageIds,
      answers,
      getQuestionsForPage,
      visibilityEvaluator,
    });

    if (nextPageIndex === null) {
      await onSubmit();
      return;
    }

    setPageHistory((prev) => [...prev, currentPageIndex]);
    setCurrentPageIndex(nextPageIndex);
    setCurrentStep(
      getStepIndexForPageIndex(nextPageIndex, pageIds, getQuestionsForPage),
    );
    return;
  }

  const current = questions[currentStep];
  if (!current) {
    await onSubmit();
    return;
  }

  if (!isSharedMode) {
    const result = validateAnswersClientSide([current], answers);

    setTouchedQuestions((prev) => {
      const next = new Set(prev);
      next.add(current.ref);
      return next;
    });

    setFieldErrors((prev) => {
      const next = new Map(prev);
      if (result.errors.length > 0) {
        next.set(
          current.ref,
          result.errors[0]?.message ?? "This answer is invalid",
        );
      } else {
        next.delete(current.ref);
      }
      return next;
    });

    if (!result.valid) {
      setValidationErrors((prev) =>
        prev.filter((error) => error.questionRef !== current.ref),
      );
      setSubmissionError("Please correct the errors below before continuing.");
      return;
    }
  }

  setSubmissionError(null);

  const nextStep = currentStep < totalQuestions - 1 ? currentStep + 1 : null;

  if (nextStep === null) {
    await onSubmit();
    return;
  }

  const resolvedStep = findNextVisibleQuestionIndex({
    questions,
    startIndex: nextStep,
    answers,
    visibilityEvaluator,
  });

  if (resolvedStep === null) {
    await onSubmit();
    return;
  }

  setQuestionHistory((prev) => [...prev, currentStep]);
  setCurrentStep(resolvedStep);
}
